import React, { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NewsletterForm — riusabile.
 *
 * Props:
 *  - variant: 'full' (pagina /newsletter, verticale) | 'inline' (footer, orizzontale)
 *  - className: classi extra opzionali
 *
 * Backend: POST /api/newsletter
 * Payload: { email, first_name, last_name, gdpr_consent, honeypot }
 *
 * Nome e cognome sono OBBLIGATORI (validazione client + Pydantic min_length=1 lato server).
 */
const NewsletterForm = ({ variant = "full", className = "" }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const isInline = variant === "inline";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setStatus("error");
      setErrorMessage("Nome e cognome sono obbligatori.");
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setErrorMessage("Inserisci un'email valida.");
      return;
    }
    if (!gdpr) {
      setStatus("error");
      setErrorMessage("Devi accettare l'informativa per iscriverti.");
      return;
    }
    setStatus("loading");
    setErrorMessage("");
    try {
      const res = await axios.post(`${API}/newsletter`, {
        email,
        first_name: firstName,
        last_name: lastName,
        gdpr_consent: gdpr,
        honeypot,
      });
      if (res.data?.success) {
        setStatus("success");
        setEmail("");
        setFirstName("");
        setLastName("");
        setGdpr(false);
      } else {
        setStatus("error");
        setErrorMessage(res.data?.error || "Errore. Riprova.");
      }
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrorMessage(detail || "Errore di connessione. Riprova.");
    }
  };

  // Honeypot — sempre presente, invisibile a utenti veri
  const Honeypot = (
    <input
      type="text"
      name="website"
      value={honeypot}
      onChange={(e) => setHoneypot(e.target.value)}
      tabIndex={-1}
      autoComplete="off"
      className="absolute left-[-9999px] opacity-0 pointer-events-none"
      aria-hidden="true"
      data-testid="newsletter-honeypot"
    />
  );

  // ============ INLINE (footer) ============
  if (isInline) {
    if (status === "success") {
      return (
        <div
          className={`flex items-center gap-3 text-galactico ${className}`}
          data-testid="newsletter-success-inline"
        >
          <span className="text-lime text-xl">✓</span>
          <span className="font-condensed uppercase tracking-widest text-sm">
            Iscritto. Benvenuto a bordo.
          </span>
        </div>
      );
    }
    const inputBase =
      "px-4 py-2.5 bg-galactico/5 border border-galactico/20 text-galactico placeholder:text-galactico/40 focus:outline-none focus:border-lime transition-colors text-[15px]";
    return (
      <form
        onSubmit={handleSubmit}
        className={`w-full space-y-3 ${className}`}
        data-testid="newsletter-form-inline"
        noValidate
      >
        {Honeypot}
        {/* Nome + Cognome */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Nome"
            required
            disabled={status === "loading"}
            className={inputBase}
            data-testid="newsletter-firstname-inline"
            aria-label="Nome"
          />
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Cognome"
            required
            disabled={status === "loading"}
            className={inputBase}
            data-testid="newsletter-lastname-inline"
            aria-label="Cognome"
          />
        </div>
        {/* Email + bottone */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="la-tua@email.com"
            required
            disabled={status === "loading"}
            className={`flex-1 ${inputBase}`}
            data-testid="newsletter-email-inline"
            aria-label="Email"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="bg-lime text-dodgers font-condensed uppercase tracking-widest text-sm px-6 py-3 hover:bg-lime/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            data-testid="newsletter-submit-inline"
          >
            {status === "loading" ? "Invio…" : "Iscriviti →"}
          </button>
        </div>
        <label className="flex items-start gap-2 text-[13px] text-galactico/70 leading-snug cursor-pointer">
          <input
            type="checkbox"
            checked={gdpr}
            onChange={(e) => setGdpr(e.target.checked)}
            className="mt-[3px] accent-lime"
            data-testid="newsletter-gdpr-inline"
          />
          <span>
            Iscrivendoti accetti l'{" "}
            <a
              href="/privacy-policy"
              className="underline hover:text-lime"
              target="_blank"
              rel="noopener noreferrer"
            >
              informativa privacy
            </a>
            .
          </span>
        </label>
        {status === "error" && errorMessage && (
          <p
            className="text-[13px] text-red-400"
            data-testid="newsletter-error-inline"
          >
            {errorMessage}
          </p>
        )}
      </form>
    );
  }

  // ============ FULL (pagina /newsletter) ============
  if (status === "success") {
    return (
      <div
        className={`border border-lime bg-lime/10 p-8 text-center ${className}`}
        data-testid="newsletter-success-full"
      >
        <p className="font-display text-[28px] md:text-[32px] leading-tight text-dodgers mb-3">
          Iscrizione completata 🎉
        </p>
        <p className="text-[16px] text-mandrogni leading-relaxed">
          Riceverai la prossima newsletter OnSports nella tua casella. Niente
          spam, mai.
        </p>
      </div>
    );
  }

  const inputFullClass =
    "w-full bg-galactico border border-dodgers/20 text-dodgers placeholder:text-dodgers/30 px-4 py-3 focus:outline-none focus:border-lime transition-colors text-[16px]";

  return (
    <form
      onSubmit={handleSubmit}
      className={`w-full max-w-[480px] mx-auto ${className}`}
      data-testid="newsletter-form-full"
      noValidate
    >
      {Honeypot}
      <div className="space-y-5">
        {/* Nome + Cognome affiancati */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="newsletter-firstname"
              className="font-condensed uppercase tracking-widest text-[12px] text-dodgers mb-2 block"
            >
              Nome *
            </label>
            <input
              id="newsletter-firstname"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={status === "loading"}
              placeholder="Mario"
              className={inputFullClass}
              data-testid="newsletter-firstname-full"
            />
          </div>
          <div>
            <label
              htmlFor="newsletter-lastname"
              className="font-condensed uppercase tracking-widest text-[12px] text-dodgers mb-2 block"
            >
              Cognome *
            </label>
            <input
              id="newsletter-lastname"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              disabled={status === "loading"}
              placeholder="Rossi"
              className={inputFullClass}
              data-testid="newsletter-lastname-full"
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="newsletter-email"
            className="font-condensed uppercase tracking-widest text-[12px] text-dodgers mb-2 block"
          >
            Email *
          </label>
          <input
            id="newsletter-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={status === "loading"}
            placeholder="la-tua@email.com"
            className={inputFullClass}
            data-testid="newsletter-email-full"
          />
        </div>
        <label className="flex items-start gap-3 text-[14px] text-mandrogni leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            checked={gdpr}
            onChange={(e) => setGdpr(e.target.checked)}
            className="mt-[4px] accent-lime"
            data-testid="newsletter-gdpr-full"
            required
          />
          <span>
            Accetto l'
            <a
              href="/privacy-policy"
              className="underline hover:text-dodgers"
              target="_blank"
              rel="noopener noreferrer"
            >
              informativa privacy
            </a>{" "}
            e acconsento al trattamento dei dati per la newsletter. *
          </span>
        </label>
        <button
          type="submit"
          disabled={status === "loading"}
          className="w-full bg-lime text-dodgers font-condensed uppercase tracking-widest text-sm px-6 py-4 hover:bg-lime/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          data-testid="newsletter-submit-full"
        >
          {status === "loading" ? "Invio in corso…" : "Iscriviti →"}
        </button>
        {status === "error" && errorMessage && (
          <p
            className="text-[14px] text-red-500 text-center"
            data-testid="newsletter-error-full"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </form>
  );
};

export default NewsletterForm;
